import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { codeCsvMaxBytes, parseCodeCsv } from './codeCsv';

export function CodeCsvInput({
  direct,
  disabled,
  onStart,
  onApply,
  onPendingChange,
}: {
  direct: boolean;
  disabled: boolean;
  onStart(): void;
  onApply(codes: string): void;
  onPendingChange(pending: boolean): void;
}) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<ReturnType<typeof parseCodeCsv> | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState(false);
  const generation = useRef(0);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  useEffect(() => {
    onPendingChange(reading || !!preview || error);
    return () => onPendingChange(false);
  }, [reading, preview, error, onPendingChange]);
  const clear = () => {
    generation.current += 1;
    setReading(false);
    setPreview(null);
    setError(false);
  };
  const read = async (file: File) => {
    clear();
    const {current} = generation;
    onStart();
    setReading(true);
    try {
      if (file.size > codeCsvMaxBytes) throw new Error('size');
      const bytes = await file.arrayBuffer();
      const result = parseCodeCsv(new TextDecoder('utf-8', { fatal: true }).decode(bytes), direct);
      if (current === generation.current) setPreview(result);
    } catch {
      if (current === generation.current) setError(true);
    } finally {
      if (current === generation.current) setReading(false);
    }
  };
  return (
    <Stack spacing={1}>
      <Typography>{t('hotelCodeImport.csvHelp')}</Typography>
      <Button href="data:text/csv;charset=utf-8,hotel_code%0A1533%0A" download="hotel-codes.csv">
        {t('hotelCodeImport.csvTemplate')}
      </Button>
      <input
        type="file"
        accept=".csv,text/csv"
        aria-label={t('hotelCodeImport.csvFile')}
        disabled={disabled || reading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void read(file);
        }}
      />
      {reading && <Typography role="status">{t('hotelCodeImport.csvReading')}</Typography>}
      {error && <Alert severity="error">{t('hotelCodeImport.csvError')}</Alert>}
      {preview && (
        <>
          <Alert severity={preview.valid ? 'info' : 'warning'}>
            {t(preview.valid ? 'hotelCodeImport.csvReady' : 'hotelCodeImport.csvInvalid', { count: preview.unique })}
          </Alert>
          <Stack sx={{ maxHeight: 240, overflow: 'auto' }}>
            {preview.rows.map((row) => (
              <Typography key={row.line} sx={{ overflowWrap: 'anywhere' }}>
                {row.line} · {row.code || '—'} · {t(`hotelCodeImport.csvRow_${row.status}`)}
              </Typography>
            ))}
          </Stack>
          <Button
            disabled={disabled || !preview.valid}
            onClick={() => {
              onApply(preview.codes);
              clear();
            }}
          >
            {t('hotelCodeImport.csvApply')}
          </Button>
        </>
      )}
      {(reading || preview || error) && (
        <Button disabled={disabled} onClick={clear}>
          {t('hotelCodeImport.csvDiscard')}
        </Button>
      )}
    </Stack>
  );
}
